import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import querystring from 'querystring';
import { buildLoginHeaders } from 'src/utilis/login/buildLoginHeaders';
import { buildProxyHeaders } from 'src/utilis/login/buildProxyHeaders';
import { isoDateRegex, fleetDateFormat, getLocalStorageValue, getSessionStorageValue } from 'src/utilis/utilis';
import { StorageKeys } from 'src/types/global';

import storeModule from 'src/store';
import { setIsRedirectPopupOpenAction } from 'src/store/slices/common/commonSlice';
import { LoginActionType } from 'src/store/actions/actionType';
import { IRootReducer } from 'src/store/reducers';
import { IAccount } from 'src/types/login';
import i18n from 'src/i18n';
import { unavailableAccountsMessage, updateAccountTokens } from './accountTokens';

const getLoginState = () => (storeModule.store.getState() as unknown as IRootReducer).loginReducer;

export const proxy = 'https://myStudioProxy.y-it.co.il/ProxyService/ProxyService.svc/Proxy';

export const encryptProxy = 'https://myStudioProxy.y-it.co.il/ProxyService/ProxyService.svc/EncryptProxy';

export const newProxy = 'https://studio-proxy.y-it.co.il/ProxyService/ProxyService.svc/EncryptProxy';

export const studioProxy = 'https://studio-proxy.y-it.co.il:543/ProxyServices/EncryptProxy';

export const myWayUrl = 'https://myStudioProxy.y-it.co.il/ProxyService/ProxyService.svc/Proxy?url=https://mywayapp.y-it.co.il/MyWayApp';

export const gpsServerUrl = 'https://myStudioProxy.y-it.co.il/ProxyService/ProxyService.svc/Proxy?url=https://mydaygps.y-it.co.il/MyDayGPS';


export const TRANSIT_PROD_URL_PREFIX = 'https://interfaceserviceapi.y-it.co.il';
export const TRANSIT_STAGING_URL =
    'https://interfaceserviceapi.ebstage.y-it.co.il/MyWayApplicationsFiles/MyWayApplicationsFilesService.svc';

export const ITURAN_API_URL = 'https://c6ubzg27cb.execute-api.eu-west-1.amazonaws.com/v1';

export const EMAIL_SERVICE_URL = `${TRANSIT_PROD_URL_PREFIX}/reports/ws_send_email` as const;

export const myWayStudioServicesBaseUrl = process.env.REACT_APP_IS_PROD === '1'
    ? 'https://mywayStudioService.y-it.co.il/MyWayStudioServices'
    : 'https://mywayStudioServiceDev.y-it.co.il/MyWayStudioServices';

export const myWayStudioProxyUrl = process.env.REACT_APP_IS_PROD === '1'
    ? 'https://myStudioProxy.y-it.co.il/ProxyService/ProxyService.svc/Proxy'
    : 'https://reverseproxyoffice.y-it.co.il/1/ProxyService/ProxyService.svc/Proxy';

/**
 * Builds the URL for the proxy server to use to encrypt the URL.
 * @param {string} fcDbUrl - encrypted fcDbUrl. // eg "==AbyR3Y0xmZvADOwgjO5EjMuITMx4CO3EjLwgzLvoDc0RHa"
 * @param {string} wsEndpoint - the endpoint to use for the proxy server. // eg "ws_MyWayStudio_Get_Shifts_By_Dates"
 * @returns {string} The URL to use for the proxy server.
 */
export const buildEncriptedUrl = (fcDbUrl: string, wsEndpoint: string): string => {
    return `${encryptProxy}?url=${fcDbUrl}&action=${wsEndpoint}`;
};

const dateConvert = (key: string, value: unknown): unknown => {
    if (
        typeof value === 'string' &&
        (fleetDateFormat.test(value) || isoDateRegex.test(value)) // checking whether the date is valid
    )
        return new Date(value);

    return value;
};

export interface AxiosRequestConfigWithUUID extends AxiosRequestConfig {
    uuid?: string;
}

export interface InternalAxiosRequestConfigWithUUID extends InternalAxiosRequestConfig {
    uuid?: string;
}

export type BuildProxyOptions = {
    indexerR: string;
    accessToken: string;
    tokenType?: string;
};

export interface AxiosRequestConfigWithProxyOptions extends AxiosRequestConfig {
    proxyOptions?: BuildProxyOptions;
}

export interface InternalAxiosRequestConfigWithProxyOptions extends InternalAxiosRequestConfig {
    proxyOptions?: BuildProxyOptions;
}

const REFRESH_BUFFER_MS = 30_000;

type ProxyAuthContext = {
    indexerR: string;
    accessToken: string;
    tokenType?: string;
    expiresAt?: number;
};

export const getStoreProxyUrl = (): string => {
    const { loginReducer } = storeModule.store.getState() as unknown as IRootReducer;
    return loginReducer?.proxyUrl || myWayStudioProxyUrl;
};

const getProxyAuthContextFromStore = (): ProxyAuthContext | null => {
    const { loginReducer } = storeModule.store.getState() as unknown as IRootReducer;
    const { indexerR, selectedFcAccount, proxyTokens } = loginReducer;

    const accountGuid = selectedFcAccount?.accountGuid;
    const proxyToken = accountGuid ? proxyTokens?.[accountGuid] : undefined;

    if (!proxyToken) return null;

    return {
        indexerR: indexerR || '',
        accessToken: proxyToken.accessToken,
        tokenType: proxyToken.tokenType,
        expiresAt: proxyToken.expiresAt,
    };
};

const instance = axios.create({
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'system': 'MyWayStudio',
        'request_origin': 'YIT',
        'User-Agent': 'ituran-external-client-yit'
    },
    transformResponse: (res) => {
        try {
            return JSON.parse(res, dateConvert);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error, res);
            return res;
        }
    },
});

export const getInstanceWithTime = (timeout: number) => {
    return axios.create({
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            'system': 'MyWayStudio',
            'request_origin': 'YIT',
            'User-Agent': 'ituran-external-client-yit'
        },
        timeout,
        transformResponse: (res) => {
            try {
                return JSON.parse(res, dateConvert);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error, res);
                return res;
            }
        },
    });
};

instance.interceptors.request.use(
    (config: InternalAxiosRequestConfigWithUUID) => {
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

instance.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export default instance;

// const FirstProxyBaseUrl = 'https://proxy1.y-it.co.il/ProxyService/ProxyService.svc/Proxy';
// const SecondaryProxyBaseUrl = 'https://proxy2.y-it.co.il/ProxyService/ProxyService.svc/Proxy';
// const ThirdProxyBaseUrl = 'https://yitadmin.y-it.co.il/api/Customer/SendUrlRequestToProxy';

// TODO remove old instances when finished
export const loginInstance = axios.create({
    withCredentials: true,
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'system': 'MyWayStudio',
        'request_origin': 'YIT',
        'User-Agent': 'ituran-external-client-yit'
    },
    transformResponse: (res) => {
        try {
            return JSON.parse(res, dateConvert);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error, res);
            return res;
        }
    },
});

loginInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfigWithUUID) => {
        const result = await buildLoginHeaders(config);

        if (!result.headers) {
            return config;
        }

        config.headers.set('X-TOTP', result.headers['X-TOTP']);
        config.headers.set('X-Request-Signature',result.headers['X-Request-Signature']);

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

loginInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export const proxyInstance = axios.create({
    // withCredentials: true,
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'system': 'MyWayStudio',
        'request_origin': 'YIT',
        'User-Agent': 'ituran-external-client-yit'
    },
    transformResponse: (res) => {
        try {
            return JSON.parse(res, dateConvert);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error, res);
            return res;
        }
    },
});

let pendingRefresh: Promise<void> | null = null;
let isCompletelyDisconnected = false;

export const resetConnectionState = () => {
    isCompletelyDisconnected = false;
    pendingRefresh = null;
};

const doTokenRefresh = (): Promise<void> => {
    if (pendingRefresh) return pendingRefresh;

    pendingRefresh = (async () => {
        try {
            const deviceToken = getLocalStorageValue(StorageKeys.DeviceToken);
            if (!deviceToken) throw new Error('No device token');

            const refreshRes = await loginInstance.post(
                `${myWayStudioServicesBaseUrl}/Login/RefreshToken`,
                querystring.stringify({ $deviceToken: deviceToken }),
                { uuid: deviceToken } as AxiosRequestConfigWithUUID
            );

            if (refreshRes.data?.response !== '0') throw new Error('Token refresh failed');

            const loginData = refreshRes.data.data?.[0] || {};
            const fcAccounts: IAccount[] = loginData.fcAccounts || [];
            const token: string = loginData.token || '';
            const indexerR: string = loginData.indexerR || '';

            const { available, unavailable } = await updateAccountTokens(fcAccounts, (account: IAccount) =>
                proxyInstance.post(
                    getStoreProxyUrl(),
                    querystring.stringify({
                        $token: token,
                        $contactUUID: account.contactUUID,
                        webservice: 'ws_MyWayStudio_Update_Token',
                    }),
                    {
                        proxyOptions: {
                            indexerR,
                            accessToken: account.proxyToken?.accessToken || '',
                            tokenType: account.proxyToken?.tokenType || 'Bearer',
                        },
                    } as AxiosRequestConfigWithProxyOptions
                )
            );

            if (fcAccounts.length && !available.length) throw new Error('No company accepted the token');

            const selectedBefore = getLoginState()?.selectedFcAccount;

            storeModule.store.dispatch({
                type: LoginActionType.initialConfigurationSuccess,
                payload: {
                    ...loginData,
                    fcAccounts: available,
                    loginType: Number(refreshRes.data?.autoLogin) || 0,
                },
            });

            const selectedAfter = getLoginState()?.selectedFcAccount;
            const switched = !!selectedAfter && selectedAfter.contactUUID !== selectedBefore?.contactUUID;
            if (switched || unavailable.length) {
                // Loaded here, not at the top: a static import of loginAction closes an import cycle
                // that breaks the app on load ("Cannot access 'uuid' before initialization")
                const { setErrorMessage, setFcAccount } = await import('src/store/actions/loginAction');
                // The reducer moved off a company that is gone; setFcAccount runs the switch's side effects
                if (switched && selectedAfter) storeModule.store.dispatch(setFcAccount(selectedAfter));
                if (unavailable.length) {
                    storeModule.store.dispatch(
                        setErrorMessage(unavailableAccountsMessage(unavailable, i18n.t.bind(i18n)))
                    );
                }
            }
        } catch (refreshError) {
            isCompletelyDisconnected = true;
            storeModule.store.dispatch(setIsRedirectPopupOpenAction({ isRedirectPopupOpen: true, reason: 'refresh_failed' }));
            throw refreshError;
        } finally {
            pendingRefresh = null;
        }
    })();

    return pendingRefresh;
};

proxyInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfigWithProxyOptions) => {
        if (!config.proxyOptions && !isCompletelyDisconnected) {
            const ctx = getProxyAuthContextFromStore();
            if (ctx?.expiresAt && Date.now() >= ctx.expiresAt - REFRESH_BUFFER_MS) {
                await doTokenRefresh();
            }
        }

        let indexerR: string;
        let accessToken: string;
        let tokenType: string;

        if (config.proxyOptions) {
            indexerR = config.proxyOptions.indexerR || '';
            accessToken = config.proxyOptions.accessToken || '';
            tokenType = config.proxyOptions.tokenType || 'Bearer';
        } else {
            const storeContext = getProxyAuthContextFromStore();
            indexerR = storeContext?.indexerR || '';
            accessToken = storeContext?.accessToken || '';
            tokenType = storeContext?.tokenType || 'Bearer';
        }

        const parsedData = querystring.parse(config.data ?? '');
        const deviceToken = getLocalStorageValue(StorageKeys.DeviceToken) || getSessionStorageValue(StorageKeys.DeviceToken);
        const currentToken = (storeModule.store.getState() as unknown as IRootReducer).loginReducer?.token;
        const logLevelCode = Number((storeModule.store.getState() as unknown as IRootReducer).loginReducer?.logLevelCode) || 0;

        const enrichedConfig: InternalAxiosRequestConfigWithProxyOptions = {
            ...config,
            data: querystring.stringify({
                ...parsedData,
                uuid: deviceToken,
                encrypted: 0,
                logLevelCode,
                ...(!parsedData.$deviceToken && { $deviceToken: deviceToken }),
                ...(parsedData.$token && currentToken && !config.proxyOptions && { $token: currentToken }),
            }),
        };
        // console.log('indexerR', indexerR, '\naccessToken', accessToken, '\ntokenType', tokenType);
        const result = await buildProxyHeaders(enrichedConfig, { indexerR, accessToken, tokenType });

        if (!result.headers) {
            return enrichedConfig;
        }

        enrichedConfig.headers.set('X-TOTP', result.headers['X-TOTP']);
        enrichedConfig.headers.set('X-Request-Signature', result.headers['X-Request-Signature']);
        enrichedConfig.headers.set('Authorization', result.headers.Authorization);

        return enrichedConfig;
    },
    (error) => {
        return Promise.reject(error);
    }
);

proxyInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        if (
            response?.status === 200 &&
            response?.config?.url?.includes('proxy') &&
            response?.data?.response === '1' &&
            !response?.config?.data?.includes('Get_Excel_Lines_Report')
        ) {
            storeModule.store.dispatch(setIsRedirectPopupOpenAction({ isRedirectPopupOpen: true, reason: 'token_expired' }));
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status !== 401 || originalRequest?._retry) {
            return Promise.reject(error);
        }

        if (isCompletelyDisconnected) {
            return Promise.reject(error);
        }

        // Update_Token is itself part of a login or a refresh: refreshing on its 401 makes the refresh wait on
        // itself and hangs the login. A 401 there means that company is unavailable (updateAccountTokens).
        if (String(originalRequest?.data ?? '').includes('ws_MyWayStudio_Update_Token')) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        await doTokenRefresh();
        return proxyInstance(originalRequest);
    }
);
